import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { z } from "zod";
import { dateSchema } from "./date-and-time";
import { siteSchema } from "./sites";

export const equipmentAssetStatusSchema = z.enum(["available", "allocated"]);

export const equipmentAssetStatusDictionary: Record<
  z.infer<typeof equipmentAssetStatusSchema>,
  MessageDescriptor
> = {
  available: msg`Tillgänglig`,
  allocated: msg`Tilldelad`,
};

export const operationalStatusSchema = z.enum(["active", "inactive"]);

export const operationalStatusDictionary: Record<
  z.infer<typeof operationalStatusSchema>,
  MessageDescriptor
> = {
  active: msg`Aktiv`,
  inactive: msg`Inaktiv`,
};

const baseAssetSchema = z.object({
  id: z.string(),
  categoryId: z.string(),
  name: z.string(),
  comment: z.string().nullish(),
  operationalStatus: operationalStatusSchema,
});

const availableAssetSchema = baseAssetSchema.extend({
  status: z.literal(equipmentAssetStatusSchema.enum.available),
  careEpisodeId: z.null(),
  allocatedAt: z.null(),
});

const allocatedAssetSchema = baseAssetSchema.extend({
  status: z.literal(equipmentAssetStatusSchema.enum.allocated),
  careEpisodeId: z.uuid(),
  allocatedAt: dateSchema,
});

export const equipmentAssetSchema = z.discriminatedUnion("status", [
  availableAssetSchema,
  allocatedAssetSchema,
]);

export const equipmentCategorySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  site: siteSchema,
});

export const equipmentCategoryWithAssetsSchema = equipmentCategorySchema.extend(
  {
    assets: z.array(equipmentAssetSchema),
  },
);

export const assetHistoryActionSchema = z.enum(["allocated", "deallocated"]);

const assetHistoryEntrySchema = z.object({
  action: assetHistoryActionSchema,
  careEpisodeId: z.uuid(),
  occurredAt: dateSchema,
  employeeNumber: z.number().int().positive(),
});

export const assetHistorySchema = z.array(assetHistoryEntrySchema);

export const equipmentAssetsSchema = z
  .object({
    assets: z.array(equipmentAssetSchema),
    categoryMap: z.record(
      z.uuid(),
      equipmentCategorySchema.omit({ id: true, site: true }),
    ),
  })
  .refine(
    ({ assets, categoryMap }) =>
      assets.every(({ categoryId }) => categoryId in categoryMap),
    {
      /* oxlint-disable-next-line lingui/no-unlocalized-strings */
      message: "All assets must have a category in the map",
      path: ["categoryMap"],
    },
  );

export const allocatedEquipmentAssetsSchema = equipmentAssetsSchema.safeExtend({
  assets: z.array(allocatedAssetSchema),
});

export const getAssetStatusLabel = (asset: {
  status: z.infer<typeof equipmentAssetStatusSchema>;
  operationalStatus: z.infer<typeof operationalStatusSchema>;
}): MessageDescriptor =>
  asset.operationalStatus === operationalStatusSchema.enum.inactive
    ? operationalStatusDictionary.inactive
    : equipmentAssetStatusDictionary[asset.status];
